import React from 'react';
import { useSelector } from 'react-redux';
import partition from 'lodash-es/partition';
import classNames from 'clsx';

import { selectIsPaymentMethodOnInstallmentsActive } from 'app/store/data/quotationResult/selectors';
import { selectSection } from 'app/store/data/formsConfig';
import { selectIsMotor } from 'app/store/data/quotationConfig';
import { FormSectionProps } from './FormSection.interfaces';
import { FormRow } from 'app/components/FormRow';
import { SectionHeading } from 'app/components/SectionHeading';
import {
  FieldsEnum,
  FormSectionHeadingType,
  FormsEnum,
  SectionsEnum,
} from 'app/shared/enums';
import { CompactHeadingSection } from 'app/components/CompactHeadingSection';
import { ConsentsSection } from 'app/components/ConsentsSection';
import { NewCarSelectControl } from 'app/components/NewCarSelectControl';
import { isCompactSelectControl } from 'app/services/env.service';
import { CurrentQuestionProvider } from './CurrentQuestion.context';

import { useStyles } from './FormSection.styles';
import { FormSectionMessage } from '../FormSectionMessage';
import { selectIsCepik3PathActive } from 'app/store/selectors/selectIsCepik3PathActive';

export default function FormSection(props: FormSectionProps) {
  const classes = useStyles(props);
  const {
    className,
    controlsList,
    disablePatch,
    formName,
    inputClasses,
    renderValidationMessage,
    screenLabel,
    sectionKey,
    renderFormRowContainer,
    renderSectionHeading: Heading,
    isLoadingResult,
  } = props;
  const section = useSelector(selectSection({ formName, sectionKey }));
  const isMotor = useSelector(selectIsMotor);
  const isCepik3ShortPath = useSelector(selectIsCepik3PathActive);
  const isPaymentMethodOnInstallmentsActive = useSelector(
    selectIsPaymentMethodOnInstallmentsActive
  );

  const newSectionStyle =
    isPaymentMethodOnInstallmentsActive &&
    [
      SectionsEnum.PaymentSectionPayment,
      SectionsEnum.PaymentSectionPaymentMethod,
      SectionsEnum.PaymentSectionDocuments,
      SectionsEnum.PaymentSectionConsents,
    ].includes(section.key);

  return section.visible ? (
    <CurrentQuestionProvider>
      <div
        className={classNames(classes.root, className, {
          [classes.newSectionStyleRoot]: newSectionStyle,
        })}
      >
        {section.label && renderHeading()}
        {renderMessages()}
        {getContent()}
      </div>
    </CurrentQuestionProvider>
  ) : null;

  function getContent() {
    const { label, headingStyle, questions, tooltip } = section;

    const isCarSectionConsent = (questionKey: string) =>
      [
        SectionsEnum.Car1,
        SectionsEnum.ShortCalculationPath,
        SectionsEnum.MubiLeasingSectionCar1,
        SectionsEnum.FirstStepSection,
      ].includes(sectionKey) &&
      (questionKey.startsWith('consent.') ||
        questionKey.endsWith('.consent')) &&
      !questionKey.startsWith('consent.all.');

    const isPaymentSectionConsent = (questionKey: string) =>
      sectionKey === SectionsEnum.PaymentSectionConsents &&
      !questionKey.startsWith('consent.all.');

    const isTerminationGeneratorSectionConsent = (questionKey: string) =>
      sectionKey === SectionsEnum.TerminationGeneratorConsents &&
      questionKey !== FieldsEnum.ButtonConsentsTerminationGenerator;

    const isMubiLeasingSectionConsent = (questionKey: string) =>
      sectionKey === SectionsEnum.MubiLeasingSectionConsents &&
      (questionKey.startsWith('consent.') ||
        questionKey.endsWith('.consent')) &&
      !questionKey.startsWith('consent.all.');

    const [consentsQuestions, nonConsentQuestions] = partition(
      questions,
      (questionKey) =>
        isCarSectionConsent(questionKey) ||
        isPaymentSectionConsent(questionKey) ||
        isTerminationGeneratorSectionConsent(questionKey) ||
        isMubiLeasingSectionConsent(questionKey)
    );

    const carDetailsQuestions = new Set<FieldsEnum>([
      FieldsEnum.CarProductionYear,
      FieldsEnum.CarMake,
      FieldsEnum.CarModel,
      FieldsEnum.CarFuelType,
      FieldsEnum.CarEngineCapacity,
      FieldsEnum.CarDoorsCount,
      FieldsEnum.CarHorsePower,
      FieldsEnum.CarVersionEt,
      FieldsEnum.CarModelIe,
      FieldsEnum.CarVersionIe,
    ]);

    const renderFormRow = (questionKey: FieldsEnum) => (
      <FormRow
        key={questionKey}
        questionKey={questionKey}
        formName={formName}
        tagSectionName={
          headingStyle === FormSectionHeadingType.Screen ? label : screenLabel
        }
        inputClasses={inputClasses}
        className={classNames(classes.formRow, {
          [classes.formRowWithoutBackGround]:
            SectionsEnum.PaymentSectionPayment === sectionKey,
          [classes.formRowConsentsCepikAbTestConsent]:
            formName === FormsEnum.CalculationFirstStepForm &&
            !isCepik3ShortPath &&
            questionKey === FieldsEnum.ButtonConsentsCalculation,
          [classes.formRowConsentsCepikAbTestNumberNotKnown]:
            formName === FormsEnum.CalculationFirstStepForm &&
            !isCepik3ShortPath &&
            questionKey === FieldsEnum.CarRegistrationNumberNotKnown,
        })}
        disablePatch={disablePatch}
        controlsList={controlsList}
        renderValidationMessage={renderValidationMessage}
        renderFormRowContainer={renderFormRowContainer}
        isLoadingResult={isLoadingResult}
      />
    );

    const shouldDisplayNewCarSelect =
      ([FormsEnum.CalculationForm, FormsEnum.CompareCepikDataForm].includes(
        formName
      ) ||
        (isMotor && formName === FormsEnum.TerminationGeneratorForm)) &&
      isCompactSelectControl &&
      nonConsentQuestions.includes(FieldsEnum.CarProductionYear);

    let carControlRendered = false;

    return (
      <>
        {nonConsentQuestions.map((questionKey) => {
          const isCarField = carDetailsQuestions.has(questionKey as FieldsEnum);

          if (isCarField && shouldDisplayNewCarSelect) {
            if (carControlRendered) return null;

            carControlRendered = true;

            return <NewCarSelectControl formName={formName} />;
          }

          return renderFormRow(questionKey);
        })}

        <ConsentsSection
          questionKeys={consentsQuestions}
          formName={formName}
          tooltip={tooltip}
        />
      </>
    );
  }

  function renderMessages() {
    const { messages = [] } = section;

    return messages.map((message) => (
      <FormSectionMessage key={message.key} message={message} />
    ));
  }

  function renderHeading() {
    const { label, headingStyle = FormSectionHeadingType.Compact } = section;

    if (!label) {
      return;
    }

    if (Heading) {
      return <Heading title={label} />;
    }

    if (headingStyle === FormSectionHeadingType.Compact) {
      return (
        <CompactHeadingSection
          {...(sectionKey === SectionsEnum.CheckoutSectionContractSigner && {
            id: 'contract.signer-label',
          })}
          {...(sectionKey === SectionsEnum.CheckoutSectionInsurant2 && {
            id: 'checkout.insurant.correspondenceAddress-label',
          })}
          title={label}
          className={classes.compactSectionHeading}
        />
      );
    } else {
      return (
        <SectionHeading
          {...(sectionKey === SectionsEnum.PaymentSectionPayment && {
            id: 'payment.numberOfInstallments-label',
          })}
          className={classes.heading}
          title={label}
        />
      );
    }
  }
}
